import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useMemo } from 'react';

const formatPercent = (value) => `${Number(value).toFixed(value % 1 === 0 ? 0 : 1)}%`;

const formatCount = (value) => {
  if (value >= 1000) {
    const compact = value / 1000;
    return `${compact.toFixed(compact >= 10 ? 1 : 2).replace(/\.0$/, '')}k`;
  }
  return value.toLocaleString();
};

const buildRibbonPath = (x1, x2, top1, bottom1, top2, bottom2) =>
  [
    `M ${x1} ${top1}`,
    `C ${x1 + 44} ${top1}, ${x2 - 44} ${top2}, ${x2} ${top2}`,
    `L ${x2} ${bottom2}`,
    `C ${x2 - 44} ${bottom2}, ${x1 + 44} ${bottom1}, ${x1} ${bottom1}`,
    'Z'
  ].join(' ');

const nodeLabelY = (direction, nodeY, nodeHeight) => (direction === 'up' ? nodeY + nodeHeight + 28 : nodeY - 18);

const PipelineFlowVisual = ({ model, isRTL = false }) => {
  useUiLanguage();
  const dataset = model.dataset;
  const stages = dataset?.stages || [];
  const exits = dataset?.exits || [];
  const isOfferWaterfall = model.id === 'offer_waterfall';

  const geometry = useMemo(() => {
    if (stages.length < 2) return null;

    const stageGap = isOfferWaterfall
      ? model.size === 'large' ? 260 : model.size === 'medium' ? 230 : 190
      : model.size === 'large' ? 214 : model.size === 'medium' ? 182 : 148;
    const nodeWidth = isOfferWaterfall ? 16 : 22;
    const maxStageHeight = isOfferWaterfall
      ? model.size === 'large' ? 116 : model.size === 'medium' ? 96 : 78
      : model.size === 'large' ? 176 : model.size === 'medium' ? 148 : 116;
    const minStageHeight = isOfferWaterfall ? 30 : 44;
    const maxCount = Math.max(...stages.map((stage) => stage.count), 1);
    const width = isOfferWaterfall
      ? model.size === 'large' ? 980 : model.size === 'medium' ? 860 : 760
      : 110 + (stageGap * (stages.length - 1)) + 220;

    const stageHeights = Object.fromEntries(
      stages.map((stage) => [
        stage.id,
        Math.max(minStageHeight, (stage.count / maxCount) * maxStageHeight)
      ])
    );

    const centerY = isOfferWaterfall ? maxStageHeight + 60 : maxStageHeight + 72;
    const nodes = stages.map((stage, index) => {
      const height = stageHeights[stage.id];
      const x = 34 + (index * stageGap);
      const y = centerY - (height / 2);
      return { ...stage, x, y, width: nodeWidth, height };
    });

    const transitionLayers = [];
    let minY = Infinity;
    let maxY = -Infinity;

    nodes.forEach((node, index) => {
      minY = Math.min(minY, node.y - 40);
      maxY = Math.max(maxY, node.y + node.height + 52);

      if (index === nodes.length - 1) return;

      const nextNode = nodes[index + 1];
      const direction = index % 2 === 0 ? 'up' : 'down';
      const stageExits = exits
        .filter((exit) => exit.sourceStageId === node.id)
        .sort((left, right) => right.count - left.count);
      const mainHeight = Math.max(18, Math.min(node.height - 8, nextNode.height));

      const currentMainTop = direction === 'up' ? node.y + node.height - mainHeight : node.y;
      const currentMainBottom = currentMainTop + mainHeight;
      const nextMainTop = nextNode.y;
      const nextMainBottom = nextNode.y + nextNode.height;

      const forward = {
        fromStageId: node.id,
        toStageId: nextNode.id,
        path: buildRibbonPath(node.x + node.width, nextNode.x, currentMainTop, currentMainBottom, nextMainTop, nextMainBottom),
        direction
      };

      const totalExitCount = Math.max(stageExits.reduce((sum, exit) => sum + exit.count, 0), 1);
      const availableExitHeight = Math.max(0, node.height - mainHeight);
      let exitCursor = direction === 'up' ? node.y : currentMainBottom;

      const renderedExits = stageExits.map((exit, exitIndex) => {
        const sliceHeight = stageExits.length === 1
          ? availableExitHeight
          : (availableExitHeight * (exit.count / totalExitCount));
        const clampedSlice = Math.max(14, sliceHeight || 18);
        const sourceTop = direction === 'up' ? exitCursor : exitCursor;
        const sourceBottom = sourceTop + clampedSlice;
        const exitX = node.x + (stageGap * (isOfferWaterfall ? 0.46 : 0.62));
        const stackedOffset = stageExits
          .slice(0, exitIndex)
          .reduce((sum, previous) => {
            const previousHeight = stageExits.length === 1
              ? availableExitHeight
              : Math.max(14, availableExitHeight * (previous.count / totalExitCount));
            return sum + previousHeight + 14;
          }, 0);
        const exitY = direction === 'up'
          ? node.y - (isOfferWaterfall ? 18 : 28) - clampedSlice - stackedOffset
          : node.y + node.height + (isOfferWaterfall ? 18 : 28) + stackedOffset;
        const exitWidth = isOfferWaterfall ? 14 : 18;
        const path = buildRibbonPath(
          node.x + node.width,
          exitX,
          sourceTop,
          sourceBottom,
          exitY,
          exitY + clampedSlice
        );

        if (direction === 'up') {
          exitCursor += clampedSlice;
        } else {
          exitCursor += clampedSlice;
        }

        minY = Math.min(minY, exitY - 24);
        maxY = Math.max(maxY, exitY + clampedSlice + 36);

        return {
          ...exit,
          direction,
          x: exitX,
          y: exitY,
          width: exitWidth,
          height: clampedSlice,
          path
        };
      });

      transitionLayers.push({ forward, exits: renderedExits });
    });

    const normalizedMinY = Math.min(minY, 24);
    const totalHeight = Math.max(maxY - normalizedMinY + 24, isOfferWaterfall ? 260 : 0);

    return { width, height: totalHeight, normalizedMinY, nodes, transitionLayers };
  }, [exits, isOfferWaterfall, model.size, stages]);

  if (!geometry) return null;

  return (
    <div className={`analytics-visual analytics-visual-pipeline-flow${isOfferWaterfall ? ' offer-waterfall' : ''}${isRTL ? ' rtl' : ''}`}>
      <svg
        viewBox={`0 0 ${geometry.width} ${geometry.height}`}
        className="analytics-visual-pipeline-svg"
        role="img"
        aria-label={dataset.title || model.title}
      >
        <g transform={`translate(0 ${-geometry.normalizedMinY})`}>
          {geometry.transitionLayers.map((layer) => (
            <g key={`flow-${layer.forward.fromStageId}`}>
              <path d={layer.forward.path} className="analytics-visual-pipeline-ribbon main" />
              {layer.exits.map((exit) => (
                <path key={exit.id} d={exit.path} className="analytics-visual-pipeline-ribbon exit" />
              ))}
            </g>
          ))}

          {geometry.nodes.map((node, index) => {
            const direction = index === geometry.nodes.length - 1 ? 'down' : geometry.transitionLayers[index]?.forward.direction || 'down';
            return (
              <g key={node.id}>
                <rect
                  x={node.x}
                  y={node.y}
                  width={node.width}
                  height={node.height}
                  rx="4"
                  className={`analytics-visual-pipeline-node ${node.tone || 'accent'}`}
                />
                <text
                  x={node.x + node.width + 10}
                  y={nodeLabelY(direction, node.y, node.height)}
                  className="analytics-visual-pipeline-label"
                  textAnchor="start"
                >
                  <tspan x={node.x + node.width + 10} dy="0">
                    {uiText(node.label)}
                  </tspan>
                  <tspan x={node.x + node.width + 10} dy="18">
                    {formatPercent(node.conversionRate)} ({formatCount(node.count)})
                  </tspan>
                </text>
              </g>
            );
          })}

          {geometry.transitionLayers.flatMap((layer) =>
            layer.exits.map((exit) => (
              <g key={`exit-${exit.id}`}>
                <rect
                  x={exit.x}
                  y={exit.y}
                  width={exit.width}
                  height={exit.height}
                  rx="4"
                  className={`analytics-visual-pipeline-exit ${exit.tone || 'danger'}`}
                />
                <text
                  x={exit.x + exit.width + 10}
                  y={exit.y + (exit.direction === 'up' ? 0 : exit.height / 2) - 2}
                  className="analytics-visual-pipeline-exit-label"
                  textAnchor="start"
                >
                  <tspan x={exit.x + exit.width + 10} dy="0">
                    {uiText(exit.label)}
                  </tspan>
                  <tspan x={exit.x + exit.width + 10} dy="18">
                    {formatPercent(exit.rate)} ({formatCount(exit.count)})
                  </tspan>
                </text>
              </g>
            ))
          )}
        </g>
      </svg>
    </div>
  );
};

export default PipelineFlowVisual;
